// medplum-duty.js — ONLINE-PRIMARY (main-2) doctor DUTY PRESENCE (Live Queue
// Engine §3). The "I'm in / I'm out" toggle + the derived-presence read.
//
// THE MODEL (docs/opd/LIVE_QUEUE_ENGINE.md §3). Duty is DERIVED at read time from
// the record — no heartbeats, no cron:
//   • Explicit: the doctor toggles on/off → the PRIVILEGED schedule-change Bot
//     writes PractitionerRole.on-duty {status, room?, since} (same door + ladder
//     as seating; a doctor's on-duty ext is RLS-readonly to themselves).
//   • Derived ON: a consult transition this doctor authored within 30 min ⇒
//     present even without a toggle. We read the cheapest honest signal —
//     Encounter.meta.lastUpdated on any post-call Encounter (CALLED / IN_CONSULT /
//     PRESCRIBED / COMPLETED) where they are a participant — because those
//     transitions already touch the Encounter (no new resource).
// derivePresence() in queue-engine.js is the ONE pure function that combines them;
// this module is the thin FHIR read/write around it.
import { getMedplum, deriveCurrentUser } from './medplum-client.js';
import { derivePresence, readOnDuty, PRESENCE_WINDOW_MS } from './queue-engine.js';
import { effectiveRoom } from './medplum-rooms.js';
import { clinicTodayKey } from './clinic-time.js';

const OPD_STATE = 'https://healther.io/opd/state';
// Post-call states whose most-recent write is honest "this doctor is working now".
const PRESENT_STATES = new Set(['CALLED', 'IN_CONSULT', 'PRESCRIBED', 'COMPLETED']);

const unwrapBot = (out) => (out && out.returnValue) || out;

let rawConfigPromise = null;
function loadRawConfig() {
  if (!rawConfigPromise) {
    rawConfigPromise = fetch('/assets/medplum-demo.json', { cache: 'no-store' }).then((res) => {
      if (!res.ok) throw new Error('medplum-demo config missing (' + res.status + ')');
      return res.json();
    });
  }
  return rawConfigPromise;
}
async function scheduleChangeBotId() {
  const cfg = await loadRawConfig();
  const id = cfg && cfg.bots && cfg.bots.scheduleChange;
  if (!id) throw new Error('schedule-change Bot not configured (cfg.bots.scheduleChange)');
  return id;
}
function throwIfOutcome(r, fallback) {
  if (r && r.resourceType === 'OperationOutcome') {
    const issue = (r.issue || [])[0] || {};
    throw new Error((issue.details && issue.details.text) || issue.diagnostics || fallback);
  }
  return r;
}
async function execDutyBot(body, fallback) {
  const botId = await scheduleChangeBotId();
  const { medplum } = await getMedplum();
  const r = throwIfOutcome(unwrapBot(await medplum.executeBot(botId, body)), fallback);
  if (!r || r.ok !== true) throw new Error((r && r.error) || fallback);
  return r;
}

const ext = (res, url) => ((res && res.extension) || []).find((e) => e && e.url === url);

/**
 * The epoch-ms of a doctor's most recent post-call Encounter write (the derived-
 * presence signal), or NaN. Bounded search: today's Encounters where they are a
 * participant, filtered to post-call states, max meta.lastUpdated. Runs as the
 * logged-in user (RLS-scoped). Best-effort — NaN on any failure (⇒ no derived on).
 */
export async function recentConsultActivityMs(medplum, practitionerId) {
  if (!practitionerId) return NaN;
  try {
    const rows = await medplum.searchResources(
      'Encounter',
      `participant=Practitioner/${practitionerId}&_sort=-_lastUpdated&_count=20`,
    );
    let best = NaN;
    for (const e of rows || []) {
      const st = (ext(e, OPD_STATE) || {}).valueString;
      if (!PRESENT_STATES.has(st)) continue;
      const t = Date.parse((e.meta && e.meta.lastUpdated) || '');
      if (Number.isFinite(t) && (!Number.isFinite(best) || t > best)) best = t;
    }
    return best;
  } catch (_) { return NaN; }
}

/** The logged-in doctor's active PractitionerRole (with its on-duty ext + seat), or null. */
async function myRole(medplum, me) {
  try {
    return await medplum.searchOne('PractitionerRole', `practitioner=Practitioner/${me.id}&active=true`);
  } catch (_) { return null; }
}

/**
 * getMyPresence() → { status:'on'|'off', room, since, source, derived, explicit }.
 * Combines the doctor's own on-duty toggle + their recent consult activity through
 * the pure derivePresence. `explicit` is the raw stored toggle (or null) so the UI
 * can show which button was last pressed even when presence is derived.
 */
export async function getMyPresence() {
  const { medplum } = await getMedplum();
  const me = await deriveCurrentUser(medplum);
  if (!me || !me.id) return { status: 'off', room: null, since: null, source: 'none', derived: false, explicit: null };
  const role = await myRole(medplum, me);
  const lastActivityMs = await recentConsultActivityMs(medplum, me.id);
  const presence = derivePresence(role, { lastActivityMs });
  const explicit = readOnDuty(role);
  // WHERE the doctor is TODAY: an explicit fresh toggle's own room hint wins;
  // otherwise resolve the effective-today seat (per-weekday override ?? default)
  // rather than the raw location[0] derivePresence used, so a Monday-only room shows.
  const hasExplicitRoom = presence.source === 'explicit' && explicit && explicit.room;
  const eff = effectiveRoom(role, clinicTodayKey());
  const room = hasExplicitRoom ? presence.room : ((eff && eff.name) || null);
  return { ...presence, room, explicit };
}

/** Set MY own duty. status 'on'|'off'; optional room hint. Returns the Bot DTO. */
export function setMyDuty({ status, room } = {}) {
  return execDutyBot(
    { action: 'set-duty', status: String(status || ''), ...(room ? { room: String(room) } : {}) },
    'Could not update your duty status',
  );
}

/** Set a dept-mate's duty (own-self / outranking-same-dept / admin — Bot-enforced). */
export function setDoctorDuty({ doctorId, status, room } = {}) {
  return execDutyBot(
    { action: 'set-duty', doctorId: String(doctorId || ''), status: String(status || ''), ...(room ? { room: String(room) } : {}) },
    'Could not update the duty status',
  );
}

export const __dutyTest = { OPD_STATE, PRESENT_STATES, PRESENCE_WINDOW_MS };
